import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, Pause, SkipBack, SkipForward, Loader2 } from "lucide-react";
import { useAudio } from "../context/AudioContext";
import { formatTime, pad2 } from "../lib/format";

export default function PlayerBar() {
  const {
    currentTrack,
    currentIndex,
    isPlaying,
    toggle,
    next,
    prev,
    seek,
    progress,
    currentTime,
    duration,
    buffering,
  } = useAudio();

  return (
    <AnimatePresence>
      {currentTrack && (
        <motion.div
          data-testid="player-bar"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-black/85 backdrop-blur-2xl border-t border-zinc-800"
        >
          {/* progress bar across full width */}
          <div
            className="absolute -top-[1px] left-0 right-0 h-[3px] bg-white/10 cursor-pointer group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              seek((e.clientX - rect.left) / rect.width);
            }}
            data-testid="player-progress"
          >
            <div
              className="h-full bg-white relative"
              style={{ width: `${progress * 100}%` }}
            >
              <span className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>

          <div className="max-w-6xl mx-auto flex items-center gap-4 px-4 md:px-8 py-3.5">
            {/* now playing */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="font-mono text-[11px] text-zinc-500 tabular-nums hidden sm:block">
                {pad2(currentIndex + 1)}
              </span>
              <div className="min-w-0">
                <p className="font-display font-bold text-sm md:text-base text-white truncate">
                  {currentTrack.title}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  FRE3BRD
                </p>
              </div>
            </div>

            {/* controls */}
            <div className="flex items-center gap-4 md:gap-6 shrink-0">
              <button
                data-testid="player-prev"
                aria-label="Previous track"
                onClick={prev}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <SkipBack size={18} className="fill-current" />
              </button>
              <button
                data-testid="player-toggle"
                aria-label={isPlaying ? "Pause" : "Play"}
                onClick={toggle}
                className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
              >
                {buffering ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : isPlaying ? (
                  <Pause size={18} className="fill-black" />
                ) : (
                  <Play size={18} className="fill-black ml-0.5" />
                )}
              </button>
              <button
                data-testid="player-next"
                aria-label="Next track"
                onClick={next}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                <SkipForward size={18} className="fill-current" />
              </button>
            </div>

            {/* time */}
            <div className="flex-1 justify-end items-center gap-2 hidden md:flex">
              <span className="font-mono text-[11px] text-zinc-400 tabular-nums">
                {formatTime(currentTime)}
              </span>
              <span className="font-mono text-[11px] text-zinc-600">/</span>
              <span className="font-mono text-[11px] text-zinc-600 tabular-nums">
                {formatTime(duration)}
              </span>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
